import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Property, Auction } from '../../types';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

// Fix Leaflet default icon issue - use CDN URLs for reliability
const DefaultIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface PropertyMapProps {
  property?: Property | Auction;
  properties?: (Property | Auction)[];
  height?: string;
  zoom?: number;
}

function MapController({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

export function PropertyMap({ property, properties, height = '300px', zoom = 14 }: PropertyMapProps) {
  // For single property
  if (property) {
    const lat = property.location.lat || 44.8186;
    const lng = property.location.lng || 20.4570;
    const center: [number, number] = [lat, lng];
    const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

    return (
      <div>
        <div className="rounded-xl overflow-hidden" style={{ height }}>
          <MapContainer
            center={center}
            zoom={zoom}
            scrollWheelZoom={true}
            touchZoom={true}
            dragging={true}
            style={{ height: '100%', width: '100%', zIndex: 1 }}
          >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
            maxZoom={20}
          />
          <MapController center={center} zoom={zoom} />
          <Marker position={center}>
            <Popup>
              <div className="min-w-[180px]">
                <h3 className="font-bold text-sm mb-1">{property.title}</h3>
                <p className="text-amber-600 font-bold mb-1">
                  {property.price.toLocaleString()} {property.currency}
                </p>
                <p className="text-xs text-gray-600">
                  {property.location.address && `${property.location.address}, `}
                  {property.location.city}
                </p>
              </div>
            </Popup>
          </Marker>
        </MapContainer>
        </div>
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-2 text-sm text-amber-600 hover:text-amber-500 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Otvori u Google Maps
        </a>
      </div>
    );
  }

  // For multiple properties (overview map)
  if (properties && properties.length > 0) {
    // Center on first property with coordinates or default to Belgrade
    const centerProperty = properties.find(p => p.location.lat && p.location.lng) || properties[0];
    const lat = centerProperty?.location.lat || 44.8186;
    const lng = centerProperty?.location.lng || 20.4570;
    const center: [number, number] = [lat, lng];

    return (
      <div className="rounded-xl overflow-hidden" style={{ height }}>
        <MapContainer
          center={center}
          zoom={11}
          scrollWheelZoom={true}
          touchZoom={true}
          dragging={true}
          style={{ height: '100%', width: '100%', zIndex: 1 }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
            maxZoom={20}
          />
          <MapController center={center} zoom={11} />
          {properties.map((p) => {
            if (!p.location.lat || !p.location.lng) return null;

            const linkPath = p.adKind === 'Aukcija'
              ? `/aukcije/${p.id}`
              : p.adKind === 'Potražnja'
              ? `/potraznja/${p.id}`
              : `/ponuda/${p.id}`;

            return (
              <Marker key={p.id} position={[p.location.lat, p.location.lng]}>
                <Popup>
                  <div className="min-w-[180px]">
                    <h3 className="font-bold text-sm mb-1">{p.title}</h3>
                    <p className="text-amber-600 font-bold mb-1">
                      {p.price.toLocaleString()} {p.currency}
                    </p>
                    <p className="text-xs text-gray-600 mb-2">
                      {p.location.city}, {p.location.region}
                    </p>
                    <Link
                      to={linkPath}
                      className="block w-full text-center bg-amber-500 text-white py-1 px-2 rounded text-xs hover:bg-amber-600 transition-colors"
                    >
                      Pogledaj oglas
                    </Link>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    );
  }

  // Default map (Serbia centered)
  const defaultCenter: [number, number] = [44.0165, 20.9114];

  return (
    <div className="rounded-xl overflow-hidden" style={{ height }}>
      <MapContainer
        center={defaultCenter}
        zoom={7}
        scrollWheelZoom={true}
        touchZoom={true}
        dragging={true}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={20}
        />
        <MapController center={defaultCenter} zoom={7} />
      </MapContainer>
    </div>
  );
}
